import { moment } from "@msuite/utils/date";
import { z } from "@msuite/utils/lib/zod";
import { defineMutator } from "@rocicorp/zero";
import { Crews } from "../handlers/crews.handlers";
import { Guards } from "../handlers/guards.handlers";
import { TicketAssignments } from "../handlers/ticket-assignments.handlers";

export const ticketAssignmentMutators = {
	delete: defineMutator(z.object({ ticketAssignmentId: z.uuid() }), async ({ tx, args, ctx }) => {
		const ticketAssignment = await TicketAssignments.getTicketAssignmentById(
			tx,
			args.ticketAssignmentId,
		);
		if (!ticketAssignment) throw new Error();
		// Editable-day rather than the plain tenancy guard: removing work arranges the board, and
		// a published day refuses arrangement on this path exactly as on the REST one.
		await Guards.assertCrewOnEditableDay(tx, ctx, ticketAssignment.crewId);
		Guards.assertProjectWorkArrangeable(ctx, ticketAssignment.projectId);
		await tx.mutate.ticketAssignments.delete({ id: args.ticketAssignmentId });
		await Crews.cleanCrewRankings(tx, ticketAssignment.crewId);
		await TicketAssignments.cleanReference(tx, ticketAssignment.referenceId);
	}),
	insert: defineMutator(
		z.object({
			// Ids must come from the call site: the mutator runs once on the client and
			// once on the server, and a generateId() in here yields two different rows —
			// the optimistic card is then torn down and remounted when the rebase lands.
			id: z.uuid(),
			newReferenceId: z.uuid(),
			ticketId: z.uuid(),
			crewId: z.uuid(),
			// Omitted, the assignment appends below the crew's current work. Callers pass a
			// ranking only to place relative to a neighbour, the way the drag paths do.
			ranking: z.number().optional(),
			projectId: z.uuid(),
		}),
		async ({ tx, args, ctx }) => {
			// Editable-day rather than the plain tenancy guard: a published day refuses new work.
			await Guards.assertCrewOnEditableDay(tx, ctx, args.crewId);
			await Guards.assertProjectInOrg(tx, ctx, args.projectId);
			const ticket = await Guards.assertTicketInOrg(tx, ctx, args.ticketId);
			// The stored ticket decides which project's rule is asked and which project the row
			// carries — an args.projectId disagreeing with it would write a row that lies to every
			// later guard reading assignment.projectId.
			if (ticket.projectId !== args.projectId) {
				throw new Error("The named project is not the ticket's");
			}
			Guards.assertProjectWorkArrangeable(ctx, ticket.projectId);
			const referenceId = await TicketAssignments.getReferenceId(
				tx,
				args.crewId,
				args.projectId,
				args.newReferenceId,
			);
			const ranking = args.ranking ?? (await TicketAssignments.getNextRanking(tx, args.crewId));
			if (!referenceId) throw new Error();
			await tx.mutate.ticketAssignments?.insert({
				id: args.id,
				ticketId: args.ticketId,
				crewId: args.crewId,
				createdBy: ctx.userId,
				createdAt: moment().unix() as any,
				ranking,
				projectId: args.projectId,
				referenceId,
				// The plan's organisation, which assertCrewOnEditableDay has just established is
				// the caller's — assigning work belongs to whoever owns the board. Named rather
				// than left to the column default, so the row's tenancy is decided by the guard
				// that admitted the write and not by the connection that carried it.
				orgId: ctx.orgId,
			});

			await Crews.cleanCrewRankings(tx, args.crewId);
		},
	),
	updateRanking: defineMutator(
		z.object({
			assignmentId: z.uuid(),
			targetAssignmentId: z.uuid(),
			edge: z.enum(["top", "bottom"]),
		}),
		async ({ args, tx, ctx }) => {
			const moving = await TicketAssignments.getTicketAssignmentById(tx, args.assignmentId);
			if (!moving) throw new Error();
			// Editable-day rather than the plain tenancy guard: a published day refuses reordering.
			await Guards.assertCrewOnEditableDay(tx, ctx, moving.crewId);
			// Reordering arranges the moved ticket; the rule binds its project. The neighbour only
			// lends its ranking.
			Guards.assertProjectWorkArrangeable(ctx, moving.projectId);
			const ticketAssignment = await TicketAssignments.getTicketAssignmentById(
				tx,
				args.targetAssignmentId,
			);
			if (!ticketAssignment) throw new Error();
			// A reorder stays inside one crew: the moved row keeps its crew and takes the neighbour's
			// ranking, so a neighbour from another crew would number it against a list it is not in.
			// Moving work between crews is an insert.
			if (ticketAssignment.crewId !== moving.crewId) {
				throw new Error("A reorder stays inside one crew");
			}
			// The renumbering below writes the neighbour's crew, so its day is held to the same lock.
			await Guards.assertCrewOnEditableDay(tx, ctx, ticketAssignment.crewId);
			await tx.mutate.ticketAssignments.update({
				id: args.assignmentId,
				ranking: args.edge === "top" ? ticketAssignment.ranking - 1 : ticketAssignment.ranking + 1,
			});
			await Crews.cleanCrewRankings(tx, ticketAssignment.crewId);
		},
	),
};
